import { api } from './api';

export type Role = 'owner' | 'editor' | 'viewer';

export interface BoardSummary {
  id: string;
  title: string;
  role: Role;
  archived: boolean;
  assetCount: number | null;
  /** Folder on the owner's dashboard; always null for boards shared with you. */
  projectId: string | null;
  /** When the dashboard thumbnail was last uploaded, or null when there is none. */
  thumbnailAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** A dashboard folder (one level, owned by the user). */
export interface Project {
  id: string;
  name: string;
  sortOrder: number;
  createdAt: string;
}

export const listBoards = () => api.get<{ boards: BoardSummary[] }>('/boards').then((r) => r.boards);
export const listDashboard = () => api.get<{ boards: BoardSummary[]; projects: Project[] }>('/boards');
export const getBoard = (id: string) => api.get<{ board: BoardSummary }>(`/boards/${id}`).then((r) => r.board);
export const createBoard = (title: string) =>
  api.post<{ board: BoardSummary }>('/boards', { title }).then((r) => r.board);
export const updateBoard = (id: string, patch: { title?: string; archived?: boolean; projectId?: string | null }) =>
  api.patch<{ board: BoardSummary }>(`/boards/${id}`, patch).then((r) => r.board);
export const deleteBoard = (id: string) => api.delete<{ ok: true }>(`/boards/${id}`);

/**
 * Server-side copy of a board (document log, assets under new ids, thumbnail). The document
 * still references the old asset ids; see sync/duplicate.ts for the rewrite.
 */
export const duplicateBoardRemote = (id: string, title: string) =>
  api.post<{ board: BoardSummary; assetMap: Record<string, string> }>(`/boards/${id}/duplicate`, { title });

export const uploadThumbnail = (id: string, image: Blob) => {
  const form = new FormData();
  form.append('image', image, 'thumbnail');
  return api.upload<{ thumbnailAt: string }>(`/boards/${id}/thumbnail`, form);
};
export const clearThumbnail = (id: string) => api.delete<{ ok: true }>(`/boards/${id}/thumbnail`);

export const createProject = (name: string) => api.post<{ project: Project }>('/projects', { name }).then((r) => r.project);
export const updateProject = (id: string, patch: { name?: string; sortOrder?: number }) =>
  api.patch<{ project: Project }>(`/projects/${id}`, patch).then((r) => r.project);
export const deleteProject = (id: string) => api.delete<{ ok: true }>(`/projects/${id}`);

export const createInvite = () => api.post<{ code: string; expiresAt: string }>('/invites');
export const listInvites = () =>
  api.get<{ invites: { code: string; expiresAt: string }[] }>('/invites').then((r) => r.invites);
